// StatusBadge.tsx — the Axios status chip: one filled badge in the
// contrast-paired form DESIGN.md C-MSG-2 asks for.
//
// Badges are the sole exemption from "no filled semantic backgrounds", on
// the grounds that Bootstrap ships a matched foreground with the fill.
// Bootswatch does not — every `.text-bg-*` it compiles is `color: #fff`
// — so the exemption was being paid for at each call site with a
// hand-written `text="dark"`, inconsistently and only where someone
// noticed. `common/badges.css` re-pairs the foregrounds against the
// palette solver's `--color-on-*` tokens the way `buttons.css` already
// does for `--bs-btn-color`; this component is the call-site half. It
// emits the paired form and nothing else, so the hand-pairing cannot come
// back one badge at a time.
//
// The app entrypoint imports the stylesheet once:
//
//     import 'common/badges.css'
//
// Without it the chip still renders — it just falls back to Bootswatch's
// own foregrounds, which is the state this component exists to fix.
import React from 'react';

// Tones are Bootstrap's semantic set plus `neutral`. Pick by meaning, not
// by color (C-BTN-1): `neutral` is the tone for anything that isn't a
// state — a count, a tag, a name.
export type StatusTone =
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'warning'
    | 'danger'
    | 'neutral';

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
    tone?: StatusTone;
    /** Fully rounded ends. For counts and chips that read as one token. */
    pill?: boolean;
    /**
     * Status words are uppercase. Turn this off for text that is quoting
     * something — a skill key, a role name, a user's own string — which
     * keeps its source case (DESIGN.md typography).
     */
    uppercase?: boolean;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
    tone = 'neutral',
    pill = false,
    uppercase = true,
    className,
    children,
    ...rest
}) => {
    const classes = ['badge', `text-bg-${tone}`];
    if (pill) classes.push('rounded-pill');
    if (uppercase) classes.push('text-uppercase');
    if (className) classes.push(className);

    return (
        <span className={classes.join(' ')} {...rest}>
            {children}
        </span>
    );
};

export default StatusBadge;
